import { applicationLocalStorage } from '@application/application_storage_local';
import { UnitService } from '@application/unit.service';
import { UniversalConfig } from '@application/universal_config.service';
import { useResolve } from '@visiba/instantiation';
import { useMemo } from 'react';

export interface CreateNewMessageSettings {
	predefinedDescriptionOfMatter: string | null;
	predefinedReasonForVisit: number[] | null;
	redRobinSessionId: string | null;
	asAgentFor: string | null;
	respondentType: string | null;
	representativeNin: string | null;
	access: string[];
	attachFiles: boolean;
}

export interface CreateNewMessageSettingsHook {
	getSettings(): CreateNewMessageSettings;
	setSettings(flowSettings: Partial<CreateNewMessageSettings>): void;
	clearSettings(): void;
}

const DEFAULT_CREATE_NEW_MESSAGE_SETTINGS: CreateNewMessageSettings = {
	predefinedDescriptionOfMatter: null,
	predefinedReasonForVisit: null,
	redRobinSessionId: null,
	asAgentFor: null,
	respondentType: null,
	representativeNin: null,
	access: [],
	attachFiles: true,
};

/**
 * Hook to read/write Create New Message flow settings persisted in localStorage,
 * scoped by the current reception.
 */
export function useCreateNewMessageSettings(): CreateNewMessageSettingsHook {
	const universalConfig = useResolve(UniversalConfig);
	const unitService = useResolve(UnitService);
	const receptionId = unitService.getUnitNodeInTree(universalConfig.selectedUnitId).receptionId;

	return useMemo(() => {
		const getSettings = () => {
			const settings = applicationLocalStorage.get('createNewMessageSettings') ?? {};

			return {
				...DEFAULT_CREATE_NEW_MESSAGE_SETTINGS,
				...settings[receptionId],
			};
		};

		const setSettings = (flowSettings: Partial<CreateNewMessageSettings>) => {
			const settings = applicationLocalStorage.get('createNewMessageSettings') ?? {};

			settings[receptionId] = {
				...getSettings(),
				...flowSettings,
			};

			applicationLocalStorage.set('createNewMessageSettings', settings);
		};

		const clearSettings = () => {
			const settings = applicationLocalStorage.get('createNewMessageSettings') ?? {};

			delete settings[receptionId];

			applicationLocalStorage.set('createNewMessageSettings', settings);
		};

		return { getSettings, setSettings, clearSettings };
	}, [receptionId]);
}
